<template>
  <div v-if="isVisibleSource || isVisibleTarget" class="box">
    <div class="wrapper-top">
      <div class="profile-wrapper">
        <IconProfile
          :color-profile="profileDetails.colorProfile"
          :initials="profileDetails.initials"
          :is-partner-person="profileDetails.isPartnerPerson"
          :profile-details
        />
        <p class="small dim medium">
          {{ profileDetails.nickname }}
          <span class="smaller dim">{{ formattedTime }}</span>
        </p>
        <Tag v-if="isMcsSpeaker" :label="'MCS'" type="gray" />
      </div>
      <div class="wrapper-actions">
        <GeneralMessageChatComingActions
          v-if="!userStore.userPreferences.isActiveMyLanguageOnly"
          :is-visible-button-copy="showMenu"
          :is-downloading="isDownloadingSource"
          :is-playing-audio="isSourcePlaying"
          :is-top="true"
          :is-last-is-other
          :is-target="false"
          :message
          :copy-text="copyTextSrc"
          :play-audio="playSourceAudio"
        />
        <GeneralMessageChatComingActions
          v-else
          :is-visible-button-copy="showMenu"
          :is-downloading="isDownloadingTarget"
          :is-playing-audio="isTargetPlaying"
          :is-top="false"
          :is-last-is-other
          :is-target="true"
          :message
          :play-audio="playTargetAudio"
        />
      </div>
    </div>
    <div @mouseenter="handleMouseEnter" @mouseleave="showMenu = false" class="bubble-container">
      <div class="chat-frame">
        <div class="bubble-frame">
          <ConferenceMessageChatComingText
            :message="message"
            :is-source-playing
            :is-target-playing
            :is-visible-source
            :is-visible-target
            :is-last-is-other="isLastIsOther"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, provide, ref, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { isEqual } from 'lodash';
import { storeToRefs } from 'pinia';

import ConferenceMessageChatComingText from '@/components/domains/chat/chat-conference/ConferenceMessageChatComingText.vue';
import GeneralMessageChatComingActions from '@/components/domains/chat/chat-general/GeneralMessageChatComingActions.vue';
import IconProfile from '@/components/icons/IconProfile.vue';
import Tag from '@/components/shared/base/Tag.vue';
import { useMessageBubbleCommon } from '@/composables/domains/chat/useMessageBubbleCommon';
import { useProfile } from '@/composables/domains/chat/useProfile';
import { useAudioStore } from '@/stores/audio';
import { useSocketStore } from '@/stores/socket';
import { useSocketsStore } from '@/stores/sockets';
import { useToastStore } from '@/stores/toast.js';
import { useUserStore } from '@/stores/user';
import { ChatMessage } from '@/types/socket';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import {
  ij_callLogId,
  ij_isLastIsOther,
  ij_isLastMessage,
  ij_isOldMessage,
  ij_setEmptyBubbleOther,
} from '@/utils/injectionKeys.js';
import { findIndexReverse } from '@/utils/shared/utilsArray';

const socketsStore = useSocketsStore();

const { t } = useI18n();

const props = defineProps<{
  messages: ChatMessage[];
  message: ChatMessage;
  lastCallLogId: string;
}>();

const setEmptyBubbleOther = inject(ij_setEmptyBubbleOther);

const audioStore = useAudioStore();
const socketStore = useSocketStore();
const userStore = useUserStore();
const toastStore = useToastStore();

const { isSingleLanguage, audioDownloading } = storeToRefs(audioStore);

//시간 포멧
const sentAt = new Date(props.message.sentAt);
const hours = sentAt.getHours();
const minutes = sentAt.getMinutes();
const period = hours >= 12 ? 'pm' : 'am';
const formattedHours = hours % 12 || 12; // Convert 0 to 12 for 12-hour format
const formattedMinutes = minutes.toString().padStart(2, '0');
const formattedTime = `${formattedHours}:${formattedMinutes} ${period}`;

const isHidden = ref(false);

const isSourcePlaying = ref(false); // 오디오 재생중 (원어)
const isTargetPlaying = ref(false); // 오디오 재생중 (통역)
const showMenu = ref(false);

const { isEmptyTgt, isEmptySrc, handleChangeReset } = useMessageBubbleCommon({
  chatMessage: toRef(props, 'message'),
  isHidden,
  setEmptyBubble: setEmptyBubbleOther,
});

// MCS 화자 여부
const isMcsSpeaker = computed(() => socketsStore.listUserMcs.includes(props.message.senderId));

const profileDetails = computed(() => {
  const { senderId } = props.message;

  const { isPartnerPerson = false } = [...socketStore.attendees].find((attendee) => attendee.userId === senderId) || {
    isPartnerPerson: false,
  };

  return {
    senderId,
    nickname: props.message.nickname,
    initials: getInitials(props.message.nickname),
    colorProfile: getColorProfile(senderId),
    isPartnerPerson,
  };
});

const isDownloadingSource = computed(() => audioDownloading.value === props.message.callLogId && isSourcePlaying.value);
const isDownloadingTarget = computed(() => audioDownloading.value === props.message.callLogId && isTargetPlaying.value);

// 원문 보임 여부
const isVisibleSource = computed<boolean>(() => {
  const isActiveMyLanguageOnly = userStore.userPreferences.isActiveMyLanguageOnly;
  const isSameSrcTgt = isEqual(props.message.srcLanguageSet, props.message.tgtLanguageSet);
  return !isEmptySrc.value && (isSingleLanguage.value || (!isActiveMyLanguageOnly && !isSameSrcTgt));
});

// 번역문 보임 여부
const isVisibleTarget = computed(() => {
  return !isEmptyTgt.value;
});

// 상대 발화 중 이 메세지가 마지막인지
const isLastIsOther = computed(() => {
  const messageLastIsOwn = props.messages.findLast(
    (item) => !item.isOwn && item.senderId === props.message?.senderId // 내가 아닌 상대방 중에서 이 메세지의 발화자의 마지막 메세지
  );
  return messageLastIsOwn!.callLogId === props.message?.callLogId;
});

// 상대 발화 중 이 메세지가 마지막인지
const isLastMessage = computed(() => props.messages.at(-1)!.callLogId === props.message?.callLogId);

// 상대 발화 중 이 메세지가 마지막인지
const isOldMessage = computed(() => {
  if (props.messages.length < 3) return false;
  const indexReverse = findIndexReverse(props.messages, (item) => item.callLogId === props.message?.callLogId);
  if (indexReverse === null) return false;
  return indexReverse < -2;
});

const { getColorProfile, getInitials } = useProfile();

// 원문 오디오 재생
const playSourceAudio = () => {
  if (isSourcePlaying.value === true) {
    //ui 즉시 변경(중복 요청 방지)
    isSourcePlaying.value = false;
    audioStore.setPlayState(0, props.message.callLogId); //재생 중이면 정지
    return;
  }
  if (props.message.srcAudioKeys) {
    const { callLogId, srcAudioKeys } = props.message;
    audioStore.setAudioManulSrcs(false, callLogId, srcAudioKeys);
  }
};

// 번역문 오디오 재생
const playTargetAudio = () => {
  if (isTargetPlaying.value === true) {
    isTargetPlaying.value = false;
    audioStore.setPlayState(0, props.message.callLogId);
    return;
  }
  if (props.message.tgtAudioKeys) {
    const { callLogId, tgtAudioKeys } = props.message;
    audioStore.setAudioManulSrcs(true, callLogId, tgtAudioKeys);
  }
};

const resetIcons = () => {
  isSourcePlaying.value = false;
  isTargetPlaying.value = false;
};

const copyTextSrc = () => {
  const text = props.message.src.contents.map((item) => item.content).join('');
  navigator.clipboard.writeText(text);
  toastStore.info(t('copied'));
};

watch(
  () => props.message.reset,
  (resetNew) => {
    handleChangeReset(resetNew);
  },
  { immediate: true }
);

const handleMouseEnter = () => {
  const { os } = getBrowserAndDeviceInfo();
  if (os === 'iOS') return;
  showMenu.value = true;
};

watch(
  () => audioStore.isSoundOn,
  (newVal) => {
    if (!newVal) {
      resetIcons();
    }
  }
);

//재생 ui표시
watch(
  () => [audioStore.playState, audioStore.playCallLogId],
  ([newVal1, newVal2]) => {
    if (newVal1 == 1) {
      if (newVal2 === props.message.callLogId) {
        isSourcePlaying.value = true;
        isTargetPlaying.value = false;
      } else {
        resetIcons();
      }
    } else if (newVal1 == 2) {
      if (newVal2 === props.message.callLogId) {
        isSourcePlaying.value = false;
        isTargetPlaying.value = true;
      } else {
        resetIcons();
      }
    } else {
      resetIcons();
    }
  }
);

provide(ij_callLogId, props.message.callLogId);
provide(ij_isLastIsOther, isLastIsOther);
provide(ij_isLastMessage, isLastMessage);
provide(ij_isOldMessage, isOldMessage);
</script>

<style scoped lang="scss">
.hidden {
  visibility: hidden;
}

.box {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 1rem 1rem 1rem;
  height: fit-content;
  min-height: unset;
}

.box.last-message {
  min-height: 40dvh;
}

.wrapper-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.profile-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.bubble-container {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  width: 100%;
  position: relative;
}

.chat-frame {
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  width: 100%;
}

.bubble-frame {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.wrapper-actions {
  position: relative;
  height: 1.75rem;
  width: 3.75rem;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  white-space: nowrap;
  position: relative;
}

::v-deep .additional-info {
  position: relative;
  bottom: unset;
}
</style>
